import { useEffect, useMemo, useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/lib/toast';
import { coerceDateTime } from '@/utils/date';
import {
  DEFAULT_PLANNING_TIME,
  DEFAULT_PLANNING_TIMEZONE,
  combineDateAndTime,
  resolvePlannedVisitAt,
  shouldAutoUpdateReportAt,
  toIsoDateTime,
} from '@/utils/inspectionScheduling';

interface PlanningDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteName: string;
  companyId?: string;
  plannedVisitAt?: string | null;
}

export function PlanningDateDialog({
  open,
  onOpenChange,
  siteId,
  siteName,
  companyId,
  plannedVisitAt,
}: PlanningDateDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { sites, reports } = useDataProvider();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<string>(DEFAULT_PLANNING_TIME);

  useEffect(() => {
    if (!open) return;
    const resolved = resolvePlannedVisitAt({
      periodicityNextVisitAt: plannedVisitAt ?? undefined,
      periodicityNextVisit: undefined,
    });
    const parsed = coerceDateTime(resolved);
    if (parsed) {
      setDate(new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
      const hours = String(parsed.getHours()).padStart(2, '0');
      const minutes = String(parsed.getMinutes()).padStart(2, '0');
      setTime(`${hours}:${minutes}`);
    } else {
      setDate(undefined);
      setTime(DEFAULT_PLANNING_TIME);
    }
  }, [open, plannedVisitAt]);

  const resolvedOldPlanned = useMemo(() => {
    return resolvePlannedVisitAt({
      periodicityNextVisitAt: plannedVisitAt ?? undefined,
      periodicityNextVisit: undefined,
    });
  }, [plannedVisitAt]);

  const handleDateSelect = (next?: Date) => {
    setDate(next);
    if (next && !time) {
      setTime(DEFAULT_PLANNING_TIME);
    }
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const plannedDateTime = date
        ? toIsoDateTime(combineDateAndTime(date, time, DEFAULT_PLANNING_TIME))
        : null;
      if (plannedDateTime) {
        await sites.updateSite(siteId, {
          periodicityNextVisitAt: plannedDateTime,
        });
      } else {
        await sites.updateSite(siteId, {
          periodicityNextVisitAt: undefined,
          periodicityNextVisit: undefined,
        });
      }

      const draft = await reports.getDraftBySite(siteId);
      if (draft && plannedDateTime) {
        if (
          shouldAutoUpdateReportAt({
            draftReportAt: draft.reportAt,
            draftPlannedVisitAt: draft.plannedVisitAt,
            oldPlannedVisitAt: resolvedOldPlanned ?? undefined,
            draftCreatedAt: draft.createdAt,
            draftVisitDate: draft.meta.visitDate,
          })
        ) {
          await reports.upsert({
            ...draft,
            reportAt: plannedDateTime,
            updatedAt: new Date().toISOString(),
          });
        }
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.sites.detail(siteId),
      });
      if (companyId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(companyId),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.planning.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
      toast.success('Pianificazione aggiornata');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update planning date:', error);
      toast.error('Impossibile aggiornare la pianificazione');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[520px] rounded-lg border border-[var(--border-default)] bg-surface p-0 shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold normal-case tracking-normal text-text-primary">
            Pianifica sopralluogo
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4 px-6 pb-6 pt-5">
          <p className="text-sm text-text-secondary">
            Imposta la data del prossimo sopralluogo per{' '}
            <span className="font-medium text-text-primary">{siteName}</span>.
          </p>
          <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-text-secondary">Data</p>
              <DatePicker
                date={date}
                onSelect={handleDateSelect}
                placeholder="Seleziona una data"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-text-secondary">Ora</p>
              <Input
                type="time"
                value={time}
                onChange={(event) => setTime(event.target.value)}
                disabled={!date || isSubmitting}
              />
            </div>
          </div>
          <p className="text-xs text-text-tertiary">
            Fuso orario: {DEFAULT_PLANNING_TIMEZONE}. Puoi lasciare vuoto per rimuovere la
            pianificazione.
          </p>
        </DialogBody>
        <DialogFooter className="border-t border-[var(--border-subtle)] bg-surface px-6 py-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Salvataggio…' : 'Salva data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
